// Pipeline diagram — sources → ISRC normalization → unified feed.
// Pure SVG with animated particles flowing along curved paths.

import { Fragment } from 'react';

interface PipelineLeftRow {
  y: number;
  c: string;
  label: string;
  status: string;
}

interface PipelineRightRow {
  y: number;
  c: string;
  initial: string;
  name: string;
  track: string;
  pf: string;
}

interface FlowParticle {
  c: string;
  b: string;
  p: string;
}

// Both stacks: 4 rows × height 48 × stride 60, centered on the ISRC box (y=180).
const PIPELINE_LEFT: PipelineLeftRow[] = [
  { y: 66,  c: '#E08A6B', label: 'apple music', status: 'api' },
  { y: 126, c: '#A4BB85', label: 'deezer',      status: 'api' },
  { y: 186, c: '#9CC8D6', label: 'soundcloud',  status: 'api' },
  { y: 246, c: '#F0A989', label: 'youtube',     status: 'api' },
];

const PIPELINE_RIGHT: PipelineRightRow[] = [
  { y: 66,  c: '#E08A6B', initial: 'L', name: 'Léo',   track: 'Yussef Dayes — Black Classical', pf: 'SCD · now' },
  { y: 126, c: '#A4BB85', initial: 'M', name: 'Mira',  track: 'Alice Coltrane — Journey',       pf: 'APL · 12m' },
  { y: 186, c: '#9CC8D6', initial: 'S', name: 'Sami',  track: 'Floating Points — live',         pf: 'YTM · 28m' },
  { y: 246, c: '#F0A989', initial: 'N', name: 'Nawel', track: 'Elis Regina — Águas de Março',   pf: 'DZR · 1h' },
];

// Sources fan in to 4 entry points on the left edge of the ISRC box (y 150,170,190,210).
const LEFT_PATHS = [
  'M210 90  Q 350 110 500 150',
  'M210 150 Q 350 160 500 170',
  'M210 210 Q 350 200 500 190',
  'M210 270 Q 350 240 500 210',
];

// ISRC fans out symmetrically from 4 exit points on its right edge to the user feeds.
const RIGHT_PATHS = [
  'M700 150 Q 850 110 980 90',
  'M700 170 Q 850 160 980 150',
  'M700 190 Q 850 200 980 210',
  'M700 210 Q 850 240 980 270',
];

const LEFT_FLOW: FlowParticle[] = [
  { c: '#E08A6B', b: '0s',    p: LEFT_PATHS[0]! },
  { c: '#A4BB85', b: '-.6s',  p: LEFT_PATHS[1]! },
  { c: '#9CC8D6', b: '-1.2s', p: LEFT_PATHS[2]! },
  { c: '#F0A989', b: '-1.8s', p: LEFT_PATHS[3]! },
];

const RIGHT_FLOW: FlowParticle[] = [
  { c: '#E08A6B', b: '-.3s',  p: RIGHT_PATHS[0]! },
  { c: '#A4BB85', b: '-.9s',  p: RIGHT_PATHS[1]! },
  { c: '#9CC8D6', b: '-1.5s', p: RIGHT_PATHS[2]! },
  { c: '#F0A989', b: '-2.1s', p: RIGHT_PATHS[3]! },
];

export function Pipeline() {
  return (
    <>
      <PipelineMobile />
      <PipelineSvg />
    </>
  );
}

function PipelineMobile() {
  return (
    <div className="pipeline-mobile" aria-hidden="true">
      <div className="pm-stack">
        {PIPELINE_LEFT.map((s) => (
          <div key={s.label} className="pm-pill">
            <span className="pm-dot" style={{ background: s.c }} />
            <span className="pm-label">{s.label}</span>
          </div>
        ))}
      </div>
      <PipelineBranches direction="in" />
      <div className="pm-isrc">
        <span className="pm-isrc-tag">CORE / NORMALIZE</span>
        <span className="pm-isrc-id">ISRC</span>
        <span className="pm-isrc-sub">identifiant universel</span>
      </div>
      <PipelineBranches direction="out" />
      <div className="pm-stack pm-feed">
        {PIPELINE_RIGHT.map((u) => (
          <div key={u.initial} className="pm-feed-card">
            <span className="pm-av" style={{ background: u.c }}>{u.initial}</span>
            <div className="pm-feed-text">
              <span className="pm-feed-name">{u.name}</span>
              <span className="pm-feed-track">{u.track.split(' — ')[0]}</span>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function PipelineSvg() {
  return (
    <svg className="promise-svg" viewBox="0 0 1200 360" fill="none" aria-label="Pipeline : plateformes → ISRC → fil furtif">
      <defs>
        <linearGradient id="pipe" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#E08A6B" stopOpacity=".0" />
          <stop offset="20%"  stopColor="#E08A6B" stopOpacity=".9" />
          <stop offset="80%"  stopColor="#A4BB85" stopOpacity=".9" />
          <stop offset="100%" stopColor="#A4BB85" stopOpacity=".0" />
        </linearGradient>
        <linearGradient id="pipeGlow" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%"   stopColor="#E08A6B" stopOpacity=".0" />
          <stop offset="50%"  stopColor="#E08A6B" stopOpacity=".18" />
          <stop offset="100%" stopColor="#A4BB85" stopOpacity=".0" />
        </linearGradient>
      </defs>

      <g fontFamily="Geist Mono" fontSize="10" fill="#5C5648" letterSpacing="2">
        <text x="120"  y="24" textAnchor="middle">SOURCES</text>
        <text x="600"  y="24" textAnchor="middle">NORMALISATION</text>
        <text x="1080" y="24" textAnchor="middle">FIL UNIFIÉ</text>
      </g>

      <g fontFamily="Geist Mono">
        {PIPELINE_LEFT.map((r) => (
          <g key={r.y}>
            <rect x="40" y={r.y} width="160" height="48" rx="2" fill="#181C28" stroke="#2A3045" />
            <circle cx="62" cy={r.y + 24} r="6" fill={r.c} />
            <text x="80" y={r.y + 28} fontSize="11" fill="#F2E7D2">{r.label}</text>
            <text x="190" y={r.y + 28} fontSize="9" fill="#5C5648" textAnchor="end">{r.status}</text>
          </g>
        ))}
      </g>

      <g>
        <rect x="500" y="120" width="200" height="120" rx="3" fill="#181C28" stroke="#E08A6B" strokeWidth="1.5" />
        <text x="600" y="155" textAnchor="middle" fontFamily="Geist Mono" fontSize="10" fill="#5C5648" letterSpacing="2">CORE / NORMALIZE</text>
        <text x="600" y="195" textAnchor="middle" fontFamily="Geist" fontSize="38" fill="#F2E7D2" fontWeight="500" letterSpacing="-2">ISRC</text>
        <text x="600" y="218" textAnchor="middle" fontFamily="Geist Mono" fontSize="9" fill="#A4BB85" letterSpacing="2">identifiant universel</text>
      </g>

      <g fontFamily="Geist Mono">
        {PIPELINE_RIGHT.map((r) => (
          <g key={r.y}>
            <rect x="980" y={r.y} width="200" height="48" rx="2" fill="#181C28" stroke="#2A3045" />
            <circle cx="1000" cy={r.y + 24} r="9" fill={r.c} />
            <text x="1000" y={r.y + 28} textAnchor="middle" fontSize="9" fill="#11141C">{r.initial}</text>
            <text x="1018" y={r.y + 18} fontSize="10" fill="#F2E7D2">{r.name}</text>
            <text x="1018" y={r.y + 32} fontFamily="Geist" fontSize="11" fill="#C9BFA8" fontWeight="500">{r.track}</text>
            <text x="1170" y={r.y + 28} textAnchor="end" fontSize="8" fill="#5C5648">{r.pf}</text>
          </g>
        ))}
      </g>

      <g strokeLinecap="round">
        {LEFT_PATHS.map((d, i) => (
          <Fragment key={`l-${i}`}>
            <path d={d} stroke="url(#pipeGlow)" strokeWidth="14" />
            <path d={d} stroke="url(#pipe)" strokeWidth="1.5" />
          </Fragment>
        ))}
        {RIGHT_PATHS.map((d, i) => (
          <Fragment key={`r-${i}`}>
            <path d={d} stroke="url(#pipeGlow)" strokeWidth="14" />
            <path d={d} stroke="url(#pipe)" strokeWidth="1.5" />
          </Fragment>
        ))}
      </g>

      {LEFT_FLOW.map((p, i) => (
        <circle key={`lf${i}`} r="3" fill={p.c}>
          <animateMotion dur="3s" begin={p.b} repeatCount="indefinite" path={p.p} />
        </circle>
      ))}
      {RIGHT_FLOW.map((p, i) => (
        <circle key={`rf${i}`} r="3" fill={p.c}>
          <animateMotion dur="3.4s" begin={p.b} repeatCount="indefinite" path={p.p} />
        </circle>
      ))}

      <text x="40" y="345" fontFamily="Geist Mono" fontSize="10" fill="#5C5648" letterSpacing="1">FIG.02 · sources hétérogènes → identifiant universel ISRC → fil unifié</text>
    </svg>
  );
}

// SVG connector with 4 lanes that converge to or diverge from the ISRC center.
// Each lane carries an animated particle along its curved path.
function PipelineBranches({ direction }: { direction: 'in' | 'out' }) {
  const lanes = [
    { x: 50,  c: '#E08A6B', d: '0s'    },
    { x: 120, c: '#A4BB85', d: '-0.6s' },
    { x: 200, c: '#9CC8D6', d: '-1.2s' },
    { x: 270, c: '#F0A989', d: '-1.8s' },
  ];
  const path = (x: number) =>
    direction === 'in'
      ? `M${x} 0 Q ${x} 28 160 48`
      : `M160 0 Q ${x} 20 ${x} 48`;
  const gradId = `pmBranch-${direction}`;

  return (
    <svg className="pm-branches" viewBox="0 0 320 48" preserveAspectRatio="none" aria-hidden="true">
      <defs>
        <linearGradient id={gradId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%"   stopColor="#C9BFA8" stopOpacity="0" />
          <stop offset="50%"  stopColor="#C9BFA8" stopOpacity=".55" />
          <stop offset="100%" stopColor="#C9BFA8" stopOpacity="0" />
        </linearGradient>
      </defs>
      {lanes.map((l) => (
        <path key={`l-${l.x}`} d={path(l.x)} stroke={`url(#${gradId})`} strokeWidth="1" fill="none" />
      ))}
      {lanes.map((l) => (
        <circle key={`p-${l.x}`} r="2.2" fill={l.c}>
          <animateMotion dur="2.4s" repeatCount="indefinite" begin={l.d} path={path(l.x)} />
          <animate
            attributeName="opacity"
            values="0;1;1;0"
            keyTimes="0;0.18;0.82;1"
            dur="2.4s"
            begin={l.d}
            repeatCount="indefinite"
          />
        </circle>
      ))}
    </svg>
  );
}
